import React, { useState, useEffect, useCallback, useRef } from 'react';
import { userAPI, showApiError } from '../../services/api';
import './RegistrationComponents.css';

const UserRegistration = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    phoneNumber: '',
    userRole: 'USER'
  });

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const isInitialLoad = useRef(true); // 초기 로드 플래그
  const [selectedUser, setSelectedUser] = useState(null); // 선택된 사용자
  const [showEditModal, setShowEditModal] = useState(false); // 수정 모달 표시
  const [showDeleteModal, setShowDeleteModal] = useState(false); // 삭제 모달 표시

  // 사용자 목록 로드
  const loadUsers = useCallback(async (reset = false) => {
    if (loading) return;
    
    console.log('loadUsers called - reset:', reset, 'loading:', loading);
    setLoading(true);
    try {
      const condition = {
        keyword: searchKeyword,
        cursor: reset ? null : cursor,
        limit: 20
      };
      
      console.log('API call condition:', condition);
      const response = await userAPI.getAll(condition);
      
      console.log('UserRegistration - API response:', response);
      console.log('UserRegistration - Users data:', response.data);
      
      if (reset) {
        console.log('Resetting users with:', response.data);
        setUsers(response.data || []);
      } else {
        console.log('Adding to existing users:', response.data);
        setUsers(prev => {
          const newData = [...prev, ...(response.data || [])];
          console.log('New users state:', newData);
          return newData;
        });
      }

      setHasMore(response.hasNext || false);
      setCursor(response.nextCursor || null);
    } catch (error) {
      console.error('사용자 목록 로드 실패:', error);
      alert('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword, cursor]);

  // 초기 데이터 로드 (한 번만 실행)
  useEffect(() => {
    if (isInitialLoad.current) {
      console.log('useEffect triggered - initial load');
      loadUsers();
      isInitialLoad.current = false;
    }
  }, [loadUsers]); // loadUsers 의존성 유지

  // 검색
  const handleSearch = () => {
    setCursor(null);
    setHasMore(true);
    loadUsers(true);
  };

  // 엔터키 검색 핸들러
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 더 많은 데이터 로드 (무한스크롤)
  const loadMore = () => {
    if (hasMore && !loading) {
      loadUsers();
    }
  };

  // 행 클릭 핸들러
  const handleRowClick = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  // 입력 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // 전화번호 필드인 경우 포맷팅 적용
    if (name === 'phoneNumber') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const userData = {
        username: formData.username,
        password: formData.password,
        name: formData.name,
        phoneNumber: getPhoneNumberOnly(formData.phoneNumber), // 숫자만 전송
        userRole: formData.userRole
      };
      
      await userAPI.create(userData);
      alert('사용자가 성공적으로 등록되었습니다!');
      
      setFormData({
        username: '',
        password: '',
        name: '',
        phoneNumber: '',
        userRole: 'USER'
      });
      
      setCursor(null);
      setHasMore(true);
      loadUsers(true); // 목록 새로고침
    } catch (error) {
      console.error('사용자 등록 실패:', error);
      showApiError(error);
    }
  };

  // 수정 핸들러
  const handleUpdate = async (updatedData) => {
    try {
      // 백엔드 UserUpdateRequest에 맞는 데이터 구조로 변환
      const updateRequest = {
        username: updatedData.username,
        name: updatedData.name,
        phoneNumber: getPhoneNumberOnly(updatedData.phoneNumber || '') // 숫자만 전송
      };
      
      await userAPI.update(selectedUser.id, updateRequest);
      alert('사용자 정보가 성공적으로 수정되었습니다!');
      setShowEditModal(false);
      setSelectedUser(null);
      setCursor(null);
      setHasMore(true);
      loadUsers(true); // 목록 새로고침
    } catch (error) {
      console.error('사용자 수정 실패:', error);
      showApiError(error);
    }
  };

  // 삭제 실행
  const handleDeleteConfirm = async () => {
    try {
      await userAPI.delete(selectedUser.id);
      alert('사용자가 성공적으로 삭제되었습니다!');
      setShowDeleteModal(false);
      setSelectedUser(null);
      setCursor(null);
      setHasMore(true);
      loadUsers(true); // 목록 새로고침
    } catch (error) {
      console.error('사용자 삭제 실패:', error);
      showApiError(error);
    }
  };

  // 전화번호 포맷팅 함수 (xxx-xxxx-xxxx 형식)
  const formatPhoneNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/\D/g, '');
    
    // 11자리 제한
    const limitedNumbers = numbers.slice(0, 11);
    
    // xxx-xxxx-xxxx 형식으로 변환
    if (limitedNumbers.length <= 3) {
      return limitedNumbers;
    } else if (limitedNumbers.length <= 7) {
      return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3)}`;
    } else {
      return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3, 7)}-${limitedNumbers.slice(7)}`;
    }
  };

  // 전화번호에서 숫자만 추출 (백엔드 전송용)
  const getPhoneNumberOnly = (value) => {
    return value.replace(/\D/g, '');
  };

  // 전화번호 표시용 포맷팅 (이미 포맷된 값도 처리)
  const displayPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '-';
    // 이미 포맷된 경우 그대로 반환, 아니면 포맷팅
    if (phoneNumber.includes('-')) {
      return phoneNumber;
    }
    return formatPhoneNumber(phoneNumber);
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR');
  };

  if (loading && users.length === 0) {
    return (
      <div className="registration-component">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>사용자 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-component">
      <div className="component-header">
        <h2>👤 사용자 등록</h2>
        <p>새로운 사용자를 등록하고 권한을 관리합니다.</p>
      </div>

      <div className="registration-layout">
        <div className="form-section">
          <h3>새 사용자 등록</h3>
          <form onSubmit={handleSubmit} className="registration-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="username">사용자 ID *</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  placeholder="사용자 ID를 입력하세요"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">비밀번호 *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="비밀번호를 입력하세요"
                />
              </div>

              <div className="form-group">
                <label htmlFor="name">이름 *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="사용자 이름을 입력하세요"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">전화번호</label>
                <input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="010-1234-5678"
                  maxLength={13}
                />
              </div>

              <div className="form-group">
                <label htmlFor="userRole">권한 *</label>
                <select
                  id="userRole"
                  name="userRole"
                  value={formData.userRole}
                  onChange={handleInputChange}
                  required
                >
                  <option value="USER">일반 사용자</option>
                  <option value="ADMIN">관리자</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                사용자 등록
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setFormData({
                username: '', password: '', name: '', phoneNumber: '', userRole: 'USER'
              })}>
                초기화
              </button>
            </div>
          </form>
        </div>

        <div className="list-section">
          <div className="list-header">
            <h3>등록된 사용자 목록</h3>
            <div className="search-section">
              <input
                type="text"
                placeholder="사용자 ID 또는 이름으로 검색"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input"
              />
              <button onClick={handleSearch} className="btn btn-primary btn-sm">
                검색
              </button>
            </div>
          </div>

          <div className="data-table">
            <div className="table-header">
              <div className="table-cell">사용자 ID</div>
              <div className="table-cell">이름</div>
              <div className="table-cell">전화번호</div>
              <div className="table-cell">권한</div>
              <div className="table-cell">등록일</div>
            </div>
            {users.map(user => (
              <div key={user.id} className="table-row user-row" onClick={() => handleRowClick(user)}>
                <div className="table-cell">{user.username}</div>
                <div className="table-cell">{user.name}</div>
                <div className="table-cell">{displayPhoneNumber(user.phoneNumber)}</div>
                <div className="table-cell">
                  <span className={`role-badge ${user.role === 'ADMIN' ? 'admin' : 'user'}`}>
                    {user.role === 'ADMIN' ? '관리자' : '사용자'}
                  </span>
                </div>
                <div className="table-cell">{formatDate(user.createdAt)}</div>
              </div>
            ))}
            
            {loading && (
              <div className="loading-row">
                <div className="loading-spinner"></div>
                <span>로딩 중...</span>
              </div>
            )}
            
            {hasMore && !loading && (
              <div className="load-more-section">
                <button onClick={loadMore} className="btn btn-secondary">
                  더 보기
                </button>
              </div>
            )}
            
            {!hasMore && users.length > 0 && (
              <div className="no-more-data">
                <p>모든 사용자를 불러왔습니다.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 수정 모달 */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>사용자 정보 수정</h3>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>사용자 ID</label>
                  <input
                    type="text"
                    value={selectedUser.username}
                    onChange={(e) => setSelectedUser({...selectedUser, username: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>이름</label>
                  <input
                    type="text"
                    value={selectedUser.name}
                    onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>전화번호</label>
                  <input
                    type="text"
                    value={displayPhoneNumber(selectedUser.phoneNumber || '')}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setSelectedUser({...selectedUser, phoneNumber: formatted});
                    }}
                    placeholder="010-1234-5678"
                    maxLength={13}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="btn-group-left">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  취소
                </button>
              </div>
              <div className="btn-group-right">
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    setShowEditModal(false);
                    setShowDeleteModal(true);
                  }}
                >
                  삭제
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => handleUpdate(selectedUser)}
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>사용자 삭제 확인</h3>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <div className="warning-icon">⚠️</div>
                <p><strong>정말로 이 사용자를 삭제하시겠습니까?</strong></p>
                <div className="user-info">
                  <p><strong>사용자 ID:</strong> {selectedUser.username}</p>
                  <p><strong>이름:</strong> {selectedUser.name}</p>
                  <p><strong>권한:</strong> {selectedUser.role === 'ADMIN' ? '관리자' : '사용자'}</p>
                </div>
                <p className="warning-text">이 작업은 되돌릴 수 없습니다.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRegistration;